import { get, chain, map, identity } from 'lodash';
import {
  createAction,
  POST_ACTION_EDIT,
  POST_ACTION_SUBSCRIBE,
  POST_ACTION_PIN,
  POST_ACTION_SHARE,
  POST_ACTION_DELETE,
  POST_ACTION_TOGGLE_COMMENTS,
} from '@wix/communities-blog-client-common';
import {
  POST_ACTION_SHEET_VALUES,
  POST_EDITOR_ACTION_SHEET_VALUES,
  CATEGORY_ACTION_SHEET_VALUE,
  DIALOG_BUTTON_VALUES,
} from '../middleware/one-app-middleware/constants';
import { getPostActions } from '../services/post-actions';

const isPublished = (post) => get(post, 'status') === 'published';
const isScheduled = (post) => get(post, 'status') === 'scheduled';

export const SEND_TO_ONE_APP = 'oneApp/SEND';
export const sendToOneApp = createAction(SEND_TO_ONE_APP);

const createOneAppAction = (type) => (data) => sendToOneApp({ type, data });

export const locationChanged = createOneAppAction('locationChanged');

export const applicationLoaded = createOneAppAction('applicationLoaded');

export const showMemberProfile = createOneAppAction('showMemberProfile');

export const showMediaManager = createOneAppAction('showMediaManager');

export const showActionSheet = createOneAppAction('showActionSheet');

export const openUrl = createOneAppAction('openUrl');

export const showInviteDialog = createOneAppAction('showInviteDialog');

export const postDraftSaved = createOneAppAction('postDraftSaved');

export const categoryCreated = createOneAppAction('categoryCreated');

export const showCategoryActionSheet = (links) =>
  showActionSheet({
    options: map(links, ({ path, text }) => ({
      label: text,
      value: { id: CATEGORY_ACTION_SHEET_VALUE, path },
    })),
    showCancelButton: true,
  });

const DESTRUCTIVE_POST_ACTION = 'delete';
const POST_ACTION_SHEET_ITEMS = {
  [POST_ACTION_SUBSCRIBE]: (post) =>
    post.isSubscribed
      ? {
          labelKey: 'post-actions.unfollow',
          id: POST_ACTION_SHEET_VALUES.UNFOLLOW,
        }
      : {
          labelKey: 'post-actions.follow',
          id: POST_ACTION_SHEET_VALUES.FOLLOW,
        },
  [POST_ACTION_EDIT]: () => ({
    labelKey: 'post-actions.edit-post',
    id: POST_ACTION_SHEET_VALUES.EDIT,
  }),
  [POST_ACTION_TOGGLE_COMMENTS]: (post) =>
    post.isCommentsDisabled
      ? {
          labelKey: 'post-actions.enable-commenting',
          id: POST_ACTION_SHEET_VALUES.ENABLE_COMMENTING,
        }
      : {
          labelKey: 'post-actions.disable-commenting',
          id: POST_ACTION_SHEET_VALUES.DISABLE_COMMENTING,
        },
  [POST_ACTION_PIN]: (post) =>
    post.isPinned
      ? {
          labelKey: 'post-actions.unpin-post',
          id: POST_ACTION_SHEET_VALUES.UNPIN,
        }
      : {
          labelKey: 'post-actions.pin-post',
          id: POST_ACTION_SHEET_VALUES.PIN,
        },
  [POST_ACTION_DELETE]: () => ({
    // eslint-disable-line quote-props
    labelKey: 'post-actions.delete-post',
    id: POST_ACTION_SHEET_VALUES.DELETE,
  }),
  [POST_ACTION_SHARE]: () => ({
    labelKey: 'post-actions.share-post',
    id: POST_ACTION_SHEET_VALUES.SHARE,
  }),
};

export const showPostActionSheet = ({ t, message, post, can, enableShare }) => {
  const { postId } = message.data.value;
  const actions = getPostActions({ post, canSee: can, enableShare });

  return showActionSheet({
    options: map(actions, (action) => {
      const { labelKey, id } = POST_ACTION_SHEET_ITEMS[action](post);
      return { label: t(labelKey), value: { id, postId } };
    }),
    showCancelButton: true,
    destructiveButtonIndex: actions.indexOf(DESTRUCTIVE_POST_ACTION),
  });
};

const POST_CREATE_ACTIONS = ['addCategory', 'saveDraft', 'publish'];
const POST_EDIT_ACTIONS = ['addCategory', 'discardChanges', 'update'];
const POST_SCHEDULED_ACTIONS = ['addCategory', 'updateScheduled', 'publishScheduled'];
const POST_EDITOR_ACTIONS_SHEET_ITEMS = {
  addCategory: () => ({
    labelKey: 'post-form-actions.category-button-label',
    value: { id: POST_EDITOR_ACTION_SHEET_VALUES.ADD_CATEGORIES },
  }),
  saveDraft: () => ({
    labelKey: 'post-form-actions.save-draft-button-label',
    value: { id: POST_EDITOR_ACTION_SHEET_VALUES.SAVE },
  }),
  publish: () => ({
    labelKey: 'post-form-actions.publish-button-label',
    value: { id: POST_EDITOR_ACTION_SHEET_VALUES.PUBLISH },
  }),
  update: () => ({
    labelKey: 'post-editor-mobile-header.update-button-label',
    value: { id: POST_EDITOR_ACTION_SHEET_VALUES.PUBLISH },
  }),
  updateScheduled: () => ({
    labelKey: 'post-form-actions.update-scheduled-button-label',
    value: { id: POST_EDITOR_ACTION_SHEET_VALUES.SAVE },
  }),
  publishScheduled: () => ({
    labelKey: 'post-form-actions.publish-scheduled-button-label',
    value: { id: POST_EDITOR_ACTION_SHEET_VALUES.PUBLISH },
  }),
  discardChanges: ({ hasUnpublishedChanges, initialPost }) =>
    hasUnpublishedChanges
      ? {
          labelKey: 'post-form-actions.discard-button-label',
          value: {
            id: POST_EDITOR_ACTION_SHEET_VALUES.DISCARD_CHANGES,
            postId: initialPost._id,
          },
        }
      : null,
};

export const showPostEditorActionSheet = ({ t, initialPost, hasUnpublishedChanges }) => {
  const actions = isPublished(initialPost)
    ? POST_EDIT_ACTIONS
    : isScheduled(initialPost)
    ? POST_SCHEDULED_ACTIONS
    : POST_CREATE_ACTIONS;

  return showActionSheet({
    options: chain(actions)
      .map((action) => POST_EDITOR_ACTIONS_SHEET_ITEMS[action]({ hasUnpublishedChanges, initialPost }))
      .filter(identity)
      .map(({ value, labelKey }) => ({ value, label: t(labelKey) }))
      .value(),
    showCancelButton: true,
  });
};

const showDialog = createOneAppAction('showDialog');

export const showPostDeleteDialog = ({ t, message }) =>
  showDialog({
    title: t('delete-post-modal.delete-post'),
    message: t('delete-post-modal.will-detele-comments'),
    options: [
      {
        label: t('delete-confirmation.cancel'),
        value: { id: DIALOG_BUTTON_VALUES.CANCEL },
      },
      {
        label: t('delete-confirmation.delete'),
        value: {
          id: DIALOG_BUTTON_VALUES.DELETE_POST,
          postId: message.data.value.postId,
        },
        type: 'destructive',
      },
    ],
  });

export const showDiscardChangesDialog = ({ t, message }) =>
  showDialog({
    title: t('discard-post-modal.discard-post'),
    message: t('discard-post-modal.are-you-sure'),
    options: [
      {
        label: t('discard-post-modal.cancel'),
        value: { id: DIALOG_BUTTON_VALUES.CANCEL },
      },
      {
        label: t('discard-post-modal.confirm'),
        value: {
          id: DIALOG_BUTTON_VALUES.DISCARD_CHANGES,
          postId: message.data.value.postId,
        },
        type: 'destructive',
      },
    ],
  });
